import React from 'react';
import responseChangelog from './response.changes.md';

import { HandleType, NodeToolbar, Position } from 'reactflow';
import { NodeConfig, NodeField, NodeFieldType, NodeInputType, NodeOutput, NodeType, ResponseNodeState } from '../../';
import { useSelector } from 'react-redux';
import { selectCommandByNodeId, State } from '@store/index';
import { Stack } from '@mui/material';
import { useNodeError } from '@common/hooks/useNodeError';
import { NodeAnchors, NodeContainer, NodeErrorToolbar, NodeHeader } from '@common/Node';
import { useNodeOutline } from '@common/hooks/useNodeOutline';

export const config: NodeConfig = {
  version: '1.1',
  title: 'Response',
  identifier: 'Response',
  command_id: 'http.response',
  background: '#00E3AA',
  icon: 'output',
  pair: 'Request',
  changelog: responseChangelog,
  anchors: [
    {
      label: 'Return',
      id: 'http.response.return',
      type: 'target' as HandleType,
      variant: 'execution',
      isHandle: true,
    },
    {
      label: 'Data',
      id: 'http.response.data',
      type: 'target' as HandleType,
      variant: 'data',
      isHandle: true,
    },
  ],
};

export const state: ResponseNodeState = {
  response_method: 'async',
  format_response: 'false',
  format_schema: { type: NodeInputType.Expression, value: `{\n   "example": "value"\n}` },
};

export const fields: NodeField[] = [
  {
    id: 'response_method',
    type: NodeFieldType.Toggle,
    options: [
      { value: 'async', label: 'Async' },
      { value: 'stream', label: 'Stream' },
    ],
    label: 'Response Method',
    description:
      'If enabled, the AI Completion node output will be streamed to the response node. This can only be used with the AI Completion node.',
    tab: '1',
  },
  {
    id: 'format_response',
    type: NodeFieldType.Toggle,
    options: [
      { value: 'true', label: 'True' },
      { value: 'false', label: 'False' },
    ],
    label: 'Format Response',
    description:
      'If enabled, you can define a custom output schema for the returned data for your workflow. This schema can utilize dynamic variables on the canvas.',
    tab: '1',
  },
  {
    id: 'format_schema',
    tab: 'node_extra',
    type: NodeFieldType.InputExpression,
    label: 'Schema',
    description: 'Define the output schema for your response node. The output must be a valid JSON object.',
  },
];

export const output: NodeOutput = {
  default: null,
  dynamic: null,
};

export const Response: React.FC<NodeType> = ({ id, selected, data }) => {
  const { title, background, anchors, icon } = config;

  const command = useSelector((state: State) => selectCommandByNodeId(state, id));

  const { isNodeError, nodeError, nodeStatus } = useNodeError({ command });
  const { nodeOutline } = useNodeOutline({ command, selected });

  if (!command) return null;
  if (command.config.command_id !== 'http.response') return null;

  const isToolbarVisible = Boolean(isNodeError);

  return (
    <NodeContainer selected={selected} nodeOutline={nodeOutline} error={false}>
      <NodeToolbar offset={18} isVisible={isToolbarVisible} position={Position.Top}>
        <Stack direction='row' spacing={1}>
          {isNodeError && <NodeErrorToolbar error={nodeError} />}
        </Stack>
      </NodeToolbar>
      <NodeHeader id={id} title={title} icon={icon} background={background} status={nodeStatus} />
      <NodeAnchors id={id} command={command} data={data} anchors={anchors} />
    </NodeContainer>
  );
};

export default { config, state, fields, output };
